import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from "@mui/material"
import { useEffect, useState } from "react"
import { CloseButton } from "../../../app/components/CloseButton"
import { SaveButton } from "../../../app/components/SaveButton"
import { useAppDispatch } from "../../../app/hooks"
import { StructureNodeTreeDto } from "../../structures/structureSlice"
import {
    createCompanyWorkflowDefinition,
    DocumentWorkflowDefinitionDetailsDto,
    updateCompanyWorkflowDefinition,
} from "./companyDocumentWorkflowSlice"
import { showError, showSuccess } from "../../notifications/notificationsSlice"

interface AddCompanyWorkflowDefinitionDialogProps {
    open: boolean
    node: StructureNodeTreeDto
    mode?: "add" | "edit"
    definitionToEdit?: DocumentWorkflowDefinitionDetailsDto | null
    onClose: () => void
    onCreated: (definition: DocumentWorkflowDefinitionDetailsDto) => void
    onUpdated?: (definition: DocumentWorkflowDefinitionDetailsDto) => void
}

export const AddCompanyWorkflowDefinitionDialog = ({
    open,
    node,
    mode = "add",
    definitionToEdit = null,
    onClose,
    onCreated,
    onUpdated,
}: AddCompanyWorkflowDefinitionDialogProps) => {
    const dispatch = useAppDispatch()
    const isEditMode = mode === "edit"

    const [newDefinitionName, setNewDefinitionName] = useState("")
    const [newDefinitionDescription, setNewDefinitionDescription] = useState("")
    const [newDefinitionChangeDescription, setNewDefinitionChangeDescription] = useState("")
    const [isCreating, setIsCreating] = useState(false)
    const [serverError, setServerError] = useState<string | null>(null)

    useEffect(() => {
        if (!open) {
            return
        }

        if (isEditMode && definitionToEdit) {
            setNewDefinitionName(definitionToEdit.name)
            setNewDefinitionDescription(definitionToEdit.description || "")
            setNewDefinitionChangeDescription(definitionToEdit.changeDescription || "")
            setServerError(null)
            return
        }

        setNewDefinitionName("")
        setNewDefinitionDescription("")
        setNewDefinitionChangeDescription("")
        setServerError(null)
    }, [open, isEditMode, definitionToEdit])

    const handleSaveDefinition = async () => {
        const trimmedName = newDefinitionName.trim()
        if (!trimmedName) {
            return
        }

        if (isEditMode && !definitionToEdit) {
            setServerError("Brak danych definicji do edycji.")
            return
        }

        setIsCreating(true)
        setServerError(null)

        try {
            if (isEditMode && definitionToEdit) {
                const result = await dispatch(updateCompanyWorkflowDefinition({
                    definitionId: definitionToEdit.id,
                    dto: {
                        name: trimmedName,
                        description: newDefinitionDescription.trim() || null,
                        changeDescription: newDefinitionChangeDescription.trim() || null,
                        organizationNodeId: definitionToEdit.scope.organizationNodeId
                            ? String(definitionToEdit.scope.organizationNodeId)
                            : null,
                        documentType: definitionToEdit.scope.documentType,
                        isDefault: definitionToEdit.scope.isDefault,
                    },
                }))

                if (updateCompanyWorkflowDefinition.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"))
                    if (onUpdated) {
                        onUpdated(result.payload)
                    }
                } else {
                    let errorMsg = "error"

                    if (result.payload && result.payload.kind === "http") {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = [problem.title, problem.detail].filter(Boolean).join(": ")
                        }
                    }

                    setServerError(errorMsg)
                }
            } else {
                const result = await dispatch(createCompanyWorkflowDefinition({
                    dto: {
                        name: trimmedName,
                        description: newDefinitionDescription.trim() || null,
                        changeDescription: newDefinitionChangeDescription.trim() || null,
                        organizationNodeId: node.id,
                        documentType: null,
                        isDefault: true,
                    },
                }))

                if (createCompanyWorkflowDefinition.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"))
                    onCreated(result.payload)
                } else {
                    let errorMsg = "error"

                    if (result.payload && result.payload.kind === "http") {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = [problem.title, problem.detail].filter(Boolean).join(": ")
                        }
                    }

                    setServerError(errorMsg)
                }
            }
        } catch {
            dispatch(showError("error"))
        } finally {
            setIsCreating(false)
        }
    }

    return (
        <Dialog open={open} fullWidth maxWidth="sm">
            <DialogTitle>{isEditMode ? "Edytuj definicję workflow" : "Dodaj definicję workflow"}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 0.5 }}>
                    <TextField
                        required
                        label="Nazwa"
                        value={newDefinitionName}
                        onChange={event => setNewDefinitionName(event.target.value)}
                        fullWidth
                    />

                    <TextField
                        label="Opis"
                        value={newDefinitionDescription}
                        onChange={event => setNewDefinitionDescription(event.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                    />

                    <TextField
                        label="Opis zmiany"
                        value={newDefinitionChangeDescription}
                        onChange={event => setNewDefinitionChangeDescription(event.target.value)}
                        multiline
                        minRows={2}
                        fullWidth
                    />

                    {serverError && (
                        <Alert sx={{ marginTop: 1 }} onClose={() => { setServerError("") }} severity="error">
                            {serverError}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <SaveButton loading={isCreating} disabled={!newDefinitionName.trim()} onClick={handleSaveDefinition} />
                <CloseButton loading={false} onClick={onClose} />
            </DialogActions>
        </Dialog>
    )
}
